import {
  type LocationWithWaitTime,
  getAllLocationsWithLatestWaitTimes,
} from "@edwt/cms/prisma";
import type { NextPage } from "next";

import Layout from "~/components/layout/DefaultLayout";
import { keystoneContext } from "~/lib/keystone";

import type { WaitTimesPage } from ".prisma/client";
import { Location } from "~/components/times";

import { DocumentRenderer } from "~/components/ui/DocumentRenderer";

/**
 * https://nextjs.org/docs/pages/building-your-application/data-fetching/get-static-props
 */
export async function getStaticProps({ req, res }) {
  if (process.env.BUILDING)
    return {
      notFound: true,
    };

  console.log(
    "⚙️ Generating static page for Legacy Wait Times route ('/legacy')",
  );

  const locationsWithWaitTimes = await getAllLocationsWithLatestWaitTimes({
    prisma: keystoneContext.prisma,
  });

  const waitTimesPage = await keystoneContext.prisma.waitTimesPage.findFirst();

  return {
    props: {
      locationsWithWaitTimes: locationsWithWaitTimes,
      waitTimesPage,
    },
    revalidate: 60 * 5, // In seconds
  };
}

/**
 * This Legacy page is statically generated and does not require any JS to view,
 * used to display wait times on older browsers that lack support for modern web applications / Next.js
 */
const Legacy: NextPage<{
  locationsWithWaitTimes: LocationWithWaitTime[];
  waitTimesPage: WaitTimesPage;
}> = ({ locationsWithWaitTimes, waitTimesPage }) => {
  const locationCountRemainder = locationsWithWaitTimes.length % 3;

  const emptyGridItemCount =
    locationCountRemainder === 0 ? 0 : 3 - locationCountRemainder;

  return (
    <Layout pageTitle="Wait Times (Legacy)" type="wide" autoRefresh={true}>
      <section className="m-auto mb-16 max-w-2xl px-3 xs:px-5">
        <h1 className="mb-10 text-2xl">Legacy Wait Times</h1>
        <p className="">
          If you were redirected to this page, you have Javascript disabled or
          are using an incompatible browser. This page provides a stripped down
          view of the emergency departments and their wait times. It will
          automatically refresh every 5 minutes.
        </p>

        {waitTimesPage && waitTimesPage.locationDisclaimer ? (
          <div className="mt-5">
            <DocumentRenderer
              /* @ts-ignore */
              document={waitTimesPage.locationDisclaimer as Element[]}
            />
          </div>
        ) : (
          false
        )}
      </section>

      <ul className="max flex flex-grow flex-wrap gap-5">
        {locationsWithWaitTimes.map((location, index) => (
          <li
            key={location.id}
            id={location.id}
            className="min-w-[min(400px,_100%)] flex-grow overflow-hidden rounded-xl border border-gray-light bg-white shadow-sm"
          >
            <Location
              location={location}
              waitTime={location.waitTime}
              lastUpdated={location.waitTime?.createdAt}
              className=""
              noJS
            />
          </li>
        ))}
        {/* Create an empty list item for every remainder  */}
        {emptyGridItemCount > 0 &&
          new Array(emptyGridItemCount)
            .fill(null)
            .map((_, index) => (
              <li
                key={"empty" + index}
                aria-hidden
                className="min-w-[min(400px,_100%)] flex-grow px-3 py-3 xs:px-5 xs:py-4"
              />
            ))}
      </ul>
    </Layout>
  );
};

export default Legacy;
